// apiHandler.ts

import axios, { AxiosProgressEvent, AxiosRequestConfig } from "axios";
import { notFound } from "next/navigation";
import { showToast } from "../components/toast/showToast";
import { getToken } from "./action/MyCookies";

const baseUrl = process.env.BASE_URL;

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

/*───────────────────────────── Header builder */
const getHeaders = async (json = true) => {
  const token = await getToken();
  const headers: Record<string, string> = {};
  if (json) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const apiRequest = async (
  method: HttpMethod,
  url: string,
  data?: unknown,
  config: AxiosRequestConfig = {}
) => {
  const fullUrl = url.startsWith("http")
    ? url
    : `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;

  const headers = await getHeaders(
    config.headers?.["Content-Type"] !== undefined
  );

  try {
    const { data: resp } = await axios({
      method,
      url: fullUrl,
      headers: { ...headers, ...(config.headers || {}) },
      data,
      ...config,
    });
    return resp;
  } catch (err) {
    throw handleApiError(err);
  }
};

// تابع مدیریت خطاها
const handleApiError = (error: any) => {
  if (error.response) {
    const statusCode = error.response.status;
    const message = error.response.data?.message || "خطایی رخ داده است";
    throw new ApiError(message, statusCode);
  } else if (error.request) {
    throw new ApiError("پاسخی از سرور دریافت نشد", 500);
  } else {
    throw new ApiError(error.message, 500);
  }
};

export const get = (url: string) => apiRequest("GET", url, undefined);
export const post = (url: string, data: unknown) =>
  apiRequest("POST", url, data);
export const put = (url: string, data: unknown) => apiRequest("PUT", url, data);
export const del = (url: string, data: unknown) =>
  apiRequest("DELETE", url, data);
export const patch = (url: string, data: unknown) =>
  apiRequest("PATCH", url, data);

export class ApiError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    Object.setPrototypeOf(this, ApiError.prototype);
    this.name = "ApiError";
    this.statusCode = statusCode;
  }
}

export const uploadImage = async (
  formData: FormData,
  onUploadProgress?: (e: AxiosProgressEvent) => void
) =>
  apiRequest("POST", "/images-upload", formData, {
    headers: await getHeaders(false), // بدون JSON
    onUploadProgress,
  });

export const handleError = (statusCode: number, message: string) => {
  switch (statusCode) {
    case 400:
      return { title: "درخواست نامعتبر", message };
    case 401:
      return { title: "عدم احراز هویت", message };
    case 403:
      return { title: "دسترسی ممنوع", message };
    case 404:
      return { title: "یافت نشد", message };
    case 500:
      return { title: "خطای سرور", message };
    default:
      return { title: `خطا ${statusCode}`, message };
  }
};

export const handleErrors = (error: unknown) => {
  console.info(error);
  if (error instanceof ApiError) {
    const { title, message } = handleError(error.statusCode, error.message);
    showToast(`${title} : ${message}`, "error");
  } else {
    showToast("مشکلی پیش آمده است", "error");
  }
};

export const handlerFetch = async (url: string) => {
  const headers = await getHeaders();

  const res = await fetch(process.env.BASE_URL + url, {
    headers: {
      ...headers,
    },
  });
  const data = await res.json();
  if (data.status === 400) return notFound();
  return data;
};
